import React, { useEffect, useState, useRef } from "react";
import { useMapbox } from "../GoogleMapsContext";

const MapComponentForDashboard = ({ trucks }) => {
  const {mapboxgl} = useMapbox();
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [selectedTruck, setSelectedTruck] = useState(null);
  const [zoomLocked, setZoomLocked] = useState(false);
  const markers = useRef([]);

  useEffect(() => {
    if (!map.current) {
      // Initialize the map
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: "mapbox://styles/mapbox/streets-v11",
        center: [0, 0], // Default center
        zoom: 2 // Default zoom
      });
      map.current.addControl(new mapboxgl.NavigationControl());
    }

    return () => {
      if (map.current) {
        map.current.remove();
      }
    };
  }, []);

  useEffect(() => {
    if (map.current && trucks.length) {
      // Remove existing markers
      markers.current.forEach(marker => marker.remove());
      markers.current = [];

      // Calculate bounds
      const bounds = new mapboxgl.LngLatBounds();
      
      trucks.forEach(truck => {
        if (truck.location) {
          const lngLat = [truck.location.longitude, truck.location.latitude];
          bounds.extend(lngLat);

          // Create a marker for each truck
          const el = document.createElement('div');
          el.className = 'truck-marker';
          el.style.backgroundImage = 'url(./assets/images/truck-icon.png)';
          el.style.width = '40px';
          el.style.height = '85px';
          el.style.backgroundSize = 'contain';
          el.style.backgroundRepeat= 'no-repeat';
          el.style.cursor = 'pointer';

          const marker = new mapboxgl.Marker(el)
            .setLngLat(lngLat)
            .addTo(map.current);

          marker.getElement().addEventListener('click', () => {
            setSelectedTruck(truck);
            const fetchAddress = async () => {
              try {
                console.log(truck)
                const addr = await getAddressFromCoordinates(
                  truck.location.latitude, 
                  truck.location.longitude
                );
                console.log(addr)
                setSelectedTruck((prev)=>({...prev, address: addr}))
                // setError(null);
              } catch (err) {
                setSelectedTruck((prev)=>({...prev, address: 'Nothing Found'}))
              }
            };
            fetchAddress();
          });

          markers.current.push(marker);
        }
      });

      // Fit the map to the bounds
      map.current.fitBounds(bounds, {
        padding: 50,
        maxZoom: 12 // Prevent excessive zooming in
      });

      // Set zoom lock after a delay
      setTimeout(() => setZoomLocked(true), 1000);
    }
  }, [map.current, trucks]);
  const getAddressFromCoordinates = async (lat, lng) => {
    // Ensure Mapbox is initialized
    if (!mapboxgl.accessToken) {
      throw new Error('Mapbox access token not configured');
    }
  
    // Parse coordinates
    let latitude, longitude;
    
    if (typeof lat === 'string' && lat.includes(',')) {
      // Handle "lat,lng" string format
      const coords = lat.split(',').map(coord => parseFloat(coord.trim()));
      if (coords.length !== 2 || coords.some(isNaN)) {
        throw new Error('Invalid coordinate format. Expected "latitude,longitude"');
      }
      [latitude, longitude] = coords;
    } else {
      // Handle separate lat/lng parameters
      latitude = parseFloat(lat);
      longitude = parseFloat(lng);
      if (isNaN(latitude) || isNaN(longitude)) {
        throw new Error('Invalid latitude or longitude values');
      }
    }
  
    // Validate coordinate ranges
    if (latitude < -90 || latitude > 90) {
      throw new Error('Latitude must be between -90 and 90 degrees');
    }
    if (longitude < -180 || longitude > 180) {
      throw new Error('Longitude must be between -180 and 180 degrees');
    }
  
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?` +
        `access_token=${mapboxgl.accessToken}` +
        `&types=address,place,poi,neighborhood,locality` +
        `&language=en&limit=1`
      );
  
      if (!response.ok) {
        throw new Error(`Mapbox API request failed with status ${response.status}`);
      }
  
      const data = await response.json();
      
      if (!data.features || data.features.length === 0) {
        return 'Address not found';
      }
  
      // Return the most relevant address result
      return data.features[0].place_name;
    } catch (error) {
      console.error('Reverse geocoding error:', error);
      throw new Error(`Failed to get address: ${error.message}`);
    }
  };
  useEffect(() => {
    if (!map.current) return;

    const handleZoom = () => {
      if (zoomLocked) {
        const zoom = map.current.getZoom();
        if (zoom < 3) {
          map.current.setZoom(3); // Prevent excessive zooming out
        }
      }
    };

    map.current.on('zoom', handleZoom);

    return () => {
      if (map.current) {
        map.current.off('zoom', handleZoom);
      }
    };
  }, [zoomLocked]);
 
  return (
    <div style={{ width: "100%", height: "100%", position: "relative" }}>
      <div ref={mapContainer} style={{ width: "100%", height: 500 }} />
      
      {selectedTruck && (
        <div
          style={{
            position: "absolute",
            top: "10px",
            right: "50px",
            backgroundColor: "white",
            borderRadius: "10px",
            minWidth: "200px",
            padding: "10px",
            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
            zIndex: 1
          }}
        >
          <button
            onClick={() => setSelectedTruck(null)}
            style={{
              position: "absolute",
              top: "5px",
              right: "5px",
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: "16px"
            }}
          >
            ×
          </button>
          <p style={{ fontSize: 18, fontWeight: "bold" }}>🚛 {selectedTruck.truck_name} ({selectedTruck.truck_type})</p>
          <p style={{ fontSize: 14 }}>👤 <strong>Driver:</strong> {selectedTruck?.driver.first_name + ' ' + selectedTruck?.driver?.last_name}</p>
          <p style={{ fontSize: 14 }}>📞 <strong>Phone:</strong> {selectedTruck?.driver?.phone_number || 'N/A'}</p>
          <p style={{ fontSize: 14 }}>📍 <strong>Location:</strong> {selectedTruck?.address || selectedTruck.location.latitude+','+selectedTruck.location.longitude}</p>
        </div>
      )}
    </div>
  );
};

export default MapComponentForDashboard;